import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormInput } from '../components/checkin/FormInput';
import { FormSelect } from '../components/checkin/FormSelect';
import {
  HotelRestaurant,
  RestaurantErrors,
  createEmptyRestaurant,
} from '../types/restaurant';
import {
  CUISINE_TYPES,
  OPERATION_MODES,
  RESTAURANT_TYPES,
  RESTAURANT_STATUSES,
} from '../utils/referenceData';
import { validateRestaurantField, validateRestaurantForm } from '../utils/restaurantValidation';
import { restaurantService } from '../services/restaurantService';

function getHotelId(): string | null {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.hotel_id || null;
  } catch {
    return null;
  }
}

export default function RestaurantFormPage() {
  const navigate = useNavigate();
  const { rid } = useParams<{ rid?: string }>();
  const [formData, setFormData] = useState<HotelRestaurant>(createEmptyRestaurant());
  const [errors, setErrors] = useState<RestaurantErrors>({});
  const [loading, setLoading] = useState(!!rid);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const isEditMode = !!rid;
  const hotelId = getHotelId();

  useEffect(() => {
    if (!hotelId || !rid) {
      setLoading(false);
      return;
    }
    restaurantService
      .getById(hotelId, rid)
      .then((data) => setFormData({ ...createEmptyRestaurant(), ...data }))
      .catch(() => setGlobalError('Impossible de charger le restaurant.'))
      .finally(() => setLoading(false));
  }, [hotelId, rid]);

  const handleChange = useCallback((field: keyof HotelRestaurant, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      const err = validateRestaurantField(field, value);
      setErrors((prevErr) => {
        if (err) return { ...prevErr, [field]: err };
        const { [field]: _, ...rest } = prevErr;
        return rest;
      });
      return updated;
    });
  }, []);

  const handleNumberChange = useCallback(
    (field: keyof HotelRestaurant, value: string) => {
      handleChange(field, value === '' ? 0 : Number(value));
    },
    [handleChange]
  );

  const handleBoolChange = useCallback(
    (field: keyof HotelRestaurant, checked: boolean) => {
      handleChange(field, checked);
    },
    [handleChange]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGlobalError(null);
    setSuccessMsg(null);

    const validationErrors = validateRestaurantForm(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setGlobalError('Veuillez corriger les erreurs dans le formulaire avant de soumettre.');
      return;
    }
    if (!hotelId) {
      setGlobalError('Identifiant hôtel introuvable. Veuillez vous reconnecter.');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode && rid) {
        await restaurantService.update(hotelId, rid, formData);
        setSuccessMsg('Restaurant mis à jour avec succès.');
      } else {
        await restaurantService.create(hotelId, formData);
        setSuccessMsg('Restaurant créé avec succès.');
      }
      setTimeout(() => navigate('/admin/restaurants'), 1500);
    } catch (err: any) {
      setGlobalError(err.message || 'Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <span className="ml-4 text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Modifier le Restaurant' : 'Ajouter un Restaurant'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEditMode
            ? 'Modifiez les informations du restaurant rattaché à votre hôtel.'
            : 'Ajoutez un nouveau restaurant rattaché à votre hôtel.'}
        </p>
      </div>

      {successMsg && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">{successMsg}</div>
      )}
      {globalError && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{globalError}</div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Identification */}
        <Section title="Identification">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Nom du restaurant" name="name" value={formData.name} onChange={(v) => handleChange('name', v)} error={errors.name} required />
            <FormInput label="Nom commercial" name="commercial_name" value={formData.commercial_name} onChange={(v) => handleChange('commercial_name', v)} />
            <FormSelect label="Type de restaurant" name="restaurant_type" value={formData.restaurant_type} onChange={(v) => handleChange('restaurant_type', v)} options={RESTAURANT_TYPES} placeholder="Sélectionner..." />
            <FormSelect label="Statut" name="status" value={formData.status} onChange={(v) => handleChange('status', v)} options={RESTAURANT_STATUSES} />
            <FormSelect label="Mode d'exploitation" name="operation_mode" value={formData.operation_mode} onChange={(v) => handleChange('operation_mode', v)} options={OPERATION_MODES} placeholder="Sélectionner..." />
          </div>
        </Section>

        {/* Informations légales */}
        <Section title="Informations Légales">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="RCCM" name="rccm" value={formData.rccm} onChange={(v) => handleChange('rccm', v)} />
            <FormInput label="NIF" name="nif" value={formData.nif} onChange={(v) => handleChange('nif', v)} />
          </div>
        </Section>

        {/* Responsable */}
        <Section title="Responsable">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput label="Nom" name="manager_name" value={formData.manager_name} onChange={(v) => handleChange('manager_name', v)} />
            <FormInput label="Téléphone" name="manager_phone" type="tel" value={formData.manager_phone} onChange={(v) => handleChange('manager_phone', v)} />
            <FormInput label="Email" name="manager_email" type="email" value={formData.manager_email} onChange={(v) => handleChange('manager_email', v)} error={errors.manager_email} />
          </div>
        </Section>

        {/* Exploitation */}
        <Section title="Exploitation">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput label="Capacité d'accueil" name="capacity" type="number" value={String(formData.capacity)} onChange={(v) => handleNumberChange('capacity', v)} error={errors.capacity} />
            <FormSelect label="Type de cuisine" name="cuisine_type" value={formData.cuisine_type} onChange={(v) => handleChange('cuisine_type', v)} options={CUISINE_TYPES} placeholder="Sélectionner..." />
            <FormInput label="Horaires d'ouverture" name="opening_hours" value={formData.opening_hours} onChange={(v) => handleChange('opening_hours', v)} placeholder="Ex: 07h00 - 22h00" />
          </div>
        </Section>

        {/* Services */}
        <Section title="Services / Commodités">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <BoolToggle label="Parking" checked={formData.has_parking} onChange={(v) => handleBoolChange('has_parking', v)} />
            <BoolToggle label="WiFi" checked={formData.has_wifi} onChange={(v) => handleBoolChange('has_wifi', v)} />
            <BoolToggle label="Climatisation" checked={formData.has_ac} onChange={(v) => handleBoolChange('has_ac', v)} />
            <BoolToggle label="Terrasse" checked={formData.has_terrace} onChange={(v) => handleBoolChange('has_terrace', v)} />
          </div>
        </Section>

        {/* RH */}
        <Section title="Ressources Humaines">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Employés hommes" name="male_employees" type="number" value={String(formData.male_employees)} onChange={(v) => handleNumberChange('male_employees', v)} />
            <FormInput label="Employés femmes" name="female_employees" type="number" value={String(formData.female_employees)} onChange={(v) => handleNumberChange('female_employees', v)} />
          </div>
        </Section>

        {/* Fiscalité */}
        <Section title="Fiscalité">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <BoolToggle label="Assujetti FPT" checked={formData.is_fpt_subject} onChange={(v) => handleBoolChange('is_fpt_subject', v)} />
            </div>
            {formData.is_fpt_subject && (
              <FormInput label="Taux FPT (%)" name="fpt_rate" type="number" value={String(formData.fpt_rate)} onChange={(v) => handleNumberChange('fpt_rate', v)} error={errors.fpt_rate} />
            )}
          </div>
        </Section>

        {/* Sécurité */}
        <Section title="Sécurité et Conformité">
          <div className="grid grid-cols-2 gap-3">
            <BoolToggle label="Registre client actif" checked={formData.has_client_register} onChange={(v) => handleBoolChange('has_client_register', v)} />
            <BoolToggle label="Conformité sécurité" checked={formData.has_security_compliance} onChange={(v) => handleBoolChange('has_security_compliance', v)} />
          </div>
        </Section>

        {/* Submit */}
        <div className="flex justify-end space-x-3 mt-6">
          <button type="button" onClick={() => navigate('/admin/restaurants')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center">
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Enregistrement...
              </>
            ) : isEditMode ? 'Mettre à jour' : 'Créer le restaurant'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-6 py-3 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="px-6 py-4">{children}</div>
    </div>
  );
}

function BoolToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center space-x-2 cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}
